import {
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
  Button,
  Link,
  TextField,
  Tooltip,
  Divider,
} from "@mui/material";
import CustomTable from "global/components/CustomTable/CustomTable";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import {
  contactsOptions,
  contactsTableHeader,
  getContactStatus,
} from "screens/Contacts/ContactsConstants";
import { useEffect, useRef, useState } from "react";
import {
  convertESTtoUserLocalDateAndTime,
  debounceEventHandler,
  handleErrorMessage,
  initialPageFilterState,
  openSuccessNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import {
  exportContacts,
  fetchAllContacts,
  fetchAllContactsCount,
} from "./ContactsService";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import SearchIcon from "assets/icons1/search_icon.svg";
import contactsStyles from "./Contacts.styles";
import AddContactsModal from "./AddContacts/AddContactsModal";
import { useTheme } from "@mui/material/styles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { RouterLink } from "components/router-link";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "utils/hooks";
import { selectIsManager } from "redux/generalSlice";
import ExportIcon from "assets/icons1/export_csv_icon.svg";
import { hasAccessTo, isAdmin } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import { pageFilters } from "models/interfaces";
import { useLocation } from "react-router-dom";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

const getColors = {
  0: "#475569",
  1: "#47AA12", // Subscribed
} as { [key: number]: string };

const Contacts = () => {
  useTitle(title.CONTACTS);
  const theme = useTheme();
  const classes = contactsStyles(theme);
  const isManager = useAppSelector(selectIsManager);
  const [contacts, setContacts] = useState<string[]>([]);
  const [count, setCount] = useState<number>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addContactsModal, setAddContactsModal] = useState<boolean>(false);
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const contactTypeFromUrl = urlParams.get("category");
  const searchFromUrl = urlParams.get("search");

  const [contactsFilters, setContactsFilters] = useState<pageFilters>(
    initialPageFilterState(
      pageFromUrl,
      rowsPerPageFromUrl,
      searchFromUrl ?? "",
      contactTypeFromUrl ?? "all"
    )
  );

  useEffect(() => {
    const { page, rowsPerPage, searchText, dropdownValue } = contactsFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;
    window.history.replaceState(
      null,
      "",
      `?page=${page}&rowsPerPage=${rowsPerPage}&category=${dropdownValue}`
    );
    getTableData();
  }, [contactsFilters]);

  const hasContactAddPermission = hasAccessTo(
    strings.contact,
    strings.addPermission
  );

  const hasContactFetchPermission = hasAccessTo(
    strings.contact,
    strings.fetchPermission
  );

  const getTableData = async () => {
    const { searchText, dropdownValue, page, rowsPerPage } = contactsFilters;
    try {
      setIsLoading(true);
      const count = await fetchAllContactsCount(dropdownValue!, searchText);
      setCount(count);
      await getTableDataAccordingToPage(
        count,
        dropdownValue!,
        page,
        searchText.trim(),
        rowsPerPage
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    count: number,
    dropdownValue: string,
    page: number,
    searchText: string,
    rowsPerPage: number
  ) => {
    try {
      let contactsRes: any[] = [];
      contactsRes = await fetchAllContacts(
        page,
        rowsPerPage,
        dropdownValue,
        searchText
      );
      const tableData = convertDataToTableFormat(contactsRes);
      setContacts([...tableData]);
    } catch (error: any) {
      throw error;
    }
  };

  const dropdownHandler = (event: any) => {
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      dropdownValue: event.target.value,
    });
  };

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const getExportContacts = async () => {
    try {
      const response = await exportContacts();
      openSuccessNotification(response.message);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleDialogClose = () => {
    setAddContactsModal(false);
  };

  const getAddContactsModal = () => {
    return (
      <AddContactsModal
        handleDialogClose={() => handleDialogClose()}
        isVisible={addContactsModal}
        contactsFilters={contactsFilters}
        setContactsFilters={setContactsFilters}
      />
    );
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((contact: any) => {
      const fullName = [contact.firstName, contact.lastName]
        .filter(Boolean)
        .join(" ");
      return {
        rowId: contact.id,
        email: (
          <Stack direction="column">
            <Link
              color="text.primary"
              variant="subtitle2"
              href={`${viewpaths.viewContactViewPath}/${contact.id}`}
              component={RouterLink}
            >
              {contact.emailId}
            </Link>
          </Stack>
        ),
        name: (
          <Tooltip
            title={fullName ? fullName : "N/A"}
            arrow
            placement="top-start"
          >
            <Typography sx={{ color: "#475569" }} variant="subtitle2">
              {fullName
                ? fullName.length > 20
                  ? `${fullName.slice(0, 20)}...`
                  : fullName
                : "N/A"}
            </Typography>
          </Tooltip>
        ),
        status: (
          <Typography
            sx={{
              color: getColors[contact.status],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                ...classes.contactStatusBox,
                backgroundColor: getColors[contact.status],
              }}
            />
            {getContactStatus(contact.status)}
          </Typography>
        ),
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              contact.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
      };
    });
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack>
          <TextField
            id="contacts_search_box"
            placeholder="Search Contacts"
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            autoComplete="off"
            fullWidth
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          {isManager && (
            <>
              <Divider />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Select
                  variant="outlined"
                  id="contacts_category_dropdown"
                  value={contactsFilters.dropdownValue}
                  onChange={dropdownHandler}
                  sx={classes.dropDown}
                  disabled={isLoading}
                >
                  {contactsOptions.map((data, index: number) => (
                    <MenuItem
                      key={index}
                      value={data.value}
                      sx={classes.dropDownMenuItem}
                    >
                      {data.label}
                    </MenuItem>
                  ))}
                </Select>
                {isAdmin() && (
                  <Box>
                    <Button
                      id="download_csv_btn"
                      variant="text"
                      size="small"
                      startIcon={
                        <Stack>
                          <img src={ExportIcon} alt="Export CSV" />
                        </Stack>
                      }
                      sx={{
                        "& .MuiButton-startIcon": {
                          marginRight: "2px",
                        },
                      }}
                      disabled={isLoading}
                      onClick={() => getExportContacts()}
                    >
                      Export Contacts
                    </Button>
                  </Box>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getContactsPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Typography variant="h5" color="text.primary">
            Contacts
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <CustomInfoTooltip
              infoMessage={
                hasContactAddPermission
                  ? ""
                  : "You don't have permission to add a contact. Please contact your account administrator."
              }
              placement="top"
            >
              <Button
                id="contact_add_btn"
                variant="outlined"
                disabled={isLoading}
                onClick={() =>
                  hasContactAddPermission && setAddContactsModal(true)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: hasContactAddPermission ? 1 : 0.5,
                }}
              >
                Create a contact
              </Button>
            </CustomInfoTooltip>
            <CustomInfoTooltip
              infoMessage={
                hasContactAddPermission
                  ? ""
                  : "You don't have permission to import contacts. Please contact your account administrator."
              }
              placement="top"
            >
              <Button
                id="contact_add_btn"
                variant="contained"
                disabled={isLoading}
                onClick={() =>
                  hasContactAddPermission &&
                  history.push(viewpaths.importantContactsViewPath)
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: hasContactAddPermission ? 1 : 0.5,
                }}
              >
                Import Contacts
              </Button>
            </CustomInfoTooltip>
          </Stack>
        </Stack>
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={contactsTableHeader()}
              rows={contacts}
              page={contactsFilters.page}
              rowsPerPage={contactsFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
        {addContactsModal && getAddContactsModal()}
      </Stack>
    );
  };

  return hasContactFetchPermission ? (
    <CenteredAuthenticatedLayout>
      {getContactsPage()}
    </CenteredAuthenticatedLayout>
  ) : (
    <Unauthorized permissionDenied={hasContactFetchPermission} />
  );
};

export default Contacts;
